/**
 * Product configuration source-of-truth.
 * Each product defines BACEN series, Firestore collections, and user-visible copy.
 * Adding a new product (e.g., imóveis): create new entry with same shape + register route in App.jsx.
 */

export const products = {
  veiculos: {
    slug: 'veiculos',
    route: '/veiculos',
    nomeProduto: 'Financiamento de Veículo',
    active: true,
    priceBRL: 39.90,
    series: { PF: '25471', PJ: '25447' },
    collections: {
      PF: 'bacen_25471_pf_veiculos',
      PJ: 'bacen_25447_pj_veiculos',
      leadsPF: 'leads_pf_veiculos',
      leadsPJ: 'leads_pj_veiculos',
    },
    copy: {
      heroTitle: 'Seu financiamento de veículo paga R$ 12.000 a mais que deveria?',
      heroSubtitle: 'Análise técnica baseada nas taxas do Banco Central — PF ou PJ.',
      ctaReport: 'Gerar Relatório do Meu Financiamento',
      ctaWhatsapp: 'Falar com Especialista via WhatsApp',
      parecerAbusivoTemplate: (dataRef, economiaBRL) =>
        `Identificamos que seu contrato de financiamento de veículo possui taxas de juros que excedem o teto legal para a época (${dataRef}). Isso configura cobrança abusiva, gerando um prejuízo estimado de ${economiaBRL} ao final das parcelas.`,
      parecerConformeTemplate: (taxaPct) =>
        `Sua taxa de juros (${taxaPct}) está em conformidade com as taxas médias do mercado de financiamento de veículos para o período. Ainda assim, vale revisar tarifas e seguros embutidos no contrato.`,
      pdfTitle: 'DIAGNÓSTICO DE JUROS — FINANCIAMENTO DE VEÍCULO',
      pdfFundamento:
        'A Resolução nº 3.909/2010 do Banco Central do Brasil estabelece que as taxas de juros cobradas pelas instituições financeiras nos contratos de financiamento de veículos não podem superar 1,5 vezes a taxa média de mercado divulgada mensalmente pelo BACEN. Contratos com taxa superior ao limite legal são passíveis de revisão judicial com devolução dos valores cobrados a maior.',
      emailSubject: 'Seu Diagnóstico de Financiamento de Veículo',
      pdfMockTitle: 'PARECER TÉCNICO',
      pdfMockSubtitle: 'Análise de Juros\nAbusivos — Veículos',
      authoritySpecialization:
        'Anos de atuação focada na defesa do consumidor contra abusos do sistema financeiro em contratos de financiamento de veículos, com aplicação da Resolução BACEN 3.909/2010 e do Código de Defesa do Consumidor (venda casada de seguros, anatocismo, capitalização indevida).',
      stats: {
        contratos: '1.000+',
        contratosLabel: 'Contratos Auditados',
        recuperado: 'R$ 47.000',
        recuperadoLabel: 'Maior Caso Recuperado',
      },
      problemBullets: [
        { t: 'Cláusulas de difícil leitura', b: 'Contratos redigidos para confundir o consumidor médio com termos técnicos e cálculos opacos.' },
        { t: 'Juros sobre juros (Anatocismo)', b: 'Capitalização mensal eleva o custo efetivo total muito além da taxa nominal prometida no ato da venda.' },
        { t: 'Venda casada de seguros', b: 'Seguros embutidos sem o consentimento ou clareza, encarecendo a parcela mensal do financiamento.' },
      ],
      reviews: [
        { n: 'Igor Macedo', t: 'Recuperei R$ 18 mil do meu financiamento de carro após o diagnóstico do Dr. Cristian. A precisão do PDF foi fundamental no processo.' },
        { n: 'Ana Paula S.', t: 'Sempre achei que minha parcela estava alta, mas não tinha como provar. O relatório técnico me deu a base que eu precisava.' },
        { n: 'Diego Marques', t: 'Excelente ferramenta. O cálculo bateu exatamente com a perícia que fiz depois. Recomendo fortemente para quem tem dúvidas.' },
      ],
      faq: [
        { q: 'Como sei se meu contrato tem juros abusivos?', a: 'Comparamos a taxa de juros implícita no seu contrato com o limite mensal publicado pelo Banco Central (BACEN) na série 25471 (PF) ou 25447 (PJ). Se a taxa cobrada superar 1,5× a média do período, o contrato possui juros abusivos e cabe revisão judicial.' },
        { q: 'Quais informações preciso ter em mãos?', a: 'Valor total financiado, valor da parcela, número total de parcelas e data de assinatura do contrato. Tudo consta no contrato ou extrato do banco.' },
        { q: 'O diagnóstico tem validade legal?', a: 'Sim. É elaborado com base nas séries oficiais do BACEN e apresenta memória de cálculo detalhada. Pode ser utilizado como prova técnica em ações revisionais e processos administrativos.' },
        { q: 'Em quanto tempo recebo o diagnóstico?', a: 'Acesso imediato após confirmação do pagamento. PDF disponível na tela e enviado por e-mail. Link de download seguro válido por 7 dias.' },
        { q: 'E se meu contrato não for abusivo?', a: 'Você recebe o diagnóstico mesmo assim, com a comprovação técnica de conformidade. Útil para quem quer ter certeza antes de agir.' },
        { q: 'Precisa de advogado para usar o diagnóstico?', a: 'Não para gerar o documento. Para acionar judicialmente, recomendamos o Dr. Cristian Lessa ou advogado de sua confiança.' },
      ],
      roi: {
        prejuizoMedio: 'R$ 12.000',
        prejuizoLabel: 'Prejuízo médio detectado em contratos abusivos',
      },
    },
  },
  imoveis: {
    slug: 'imoveis',
    route: '/imoveis',
    nomeProduto: 'Financiamento Imobiliário',
    active: true,
    priceBRL: 197.00,
    series: { PF: '25499', PJ: '25488' },
    collections: {
      PF: 'bacen_25499_pf_imoveis',
      PJ: 'bacen_25488_pj_imoveis',
      leadsPF: 'leads_pf_imoveis',
      leadsPJ: 'leads_pj_imoveis',
    },
    copy: {
      heroTitle: 'Seu financiamento imobiliário pode ter R$ 87.000 de juros ilegais.',
      heroSubtitle: 'Análise técnica baseada nas taxas do Banco Central — SAC ou Price.',
      ctaReport: 'Gerar Relatório do Meu Financiamento',
      ctaWhatsapp: 'Falar com Especialista via WhatsApp',
      parecerAbusivoTemplate: (dataRef, economiaBRL) =>
        `Identificamos que seu contrato de financiamento imobiliário possui taxas de juros que excedem o teto legal para a época (${dataRef}). Isso configura cobrança abusiva, gerando um prejuízo estimado de ${economiaBRL} ao final das parcelas.`,
      parecerConformeTemplate: (taxaPct) =>
        `Sua taxa de juros (${taxaPct}) está em conformidade com as taxas médias do mercado imobiliário para o período. Ainda assim, vale revisar taxas administrativas e seguros obrigatórios (MIP e DFI).`,
      pdfTitle: 'DIAGNÓSTICO DE JUROS — FINANCIAMENTO IMOBILIÁRIO',
      pdfFundamento:
        'A análise baseia-se na Lei nº 9.514/1997 e na Lei nº 4.380/1964, que regem o Sistema Financeiro da Habitação (SFH) e o Sistema de Financiamento Imobiliário (SFI). A jurisprudência do STJ estabelece que as taxas de juros não podem ser substancialmente superiores à taxa média de mercado divulgada pelo Banco Central para operações da mesma espécie.',
      emailSubject: 'Seu Diagnóstico de Financiamento Imobiliário',
      pdfMockTitle: 'DIAGNÓSTICO IMOBILIÁRIO',
      pdfMockSubtitle: 'Análise SFH/SFI\nSistema SAC ou Price',
      authoritySpecialization:
        'Atuação especializada em Direito Imobiliário e Bancário aplicado ao SFH/SFI: análise de contratos sob a Lei 9.514/97, Lei 4.380/64 e jurisprudência do STJ. Auditoria técnica de seguros obrigatórios (MIP/DFI), cobrança indevida de tarifas, capitalização e amortização irregular em sistemas SAC e Price.',
      stats: {
        contratos: '1.000+',
        contratosLabel: 'Contratos Auditados',
        recuperado: 'R$ 312.000',
        recuperadoLabel: 'Maior Caso Recuperado',
      },
      problemBullets: [
        { t: 'MIP/DFI inflados', b: 'Seguros obrigatórios (Morte/Invalidez Permanente e Danos Físicos do Imóvel) cobrados acima da média de mercado, encarecendo cada parcela por décadas.' },
        { t: 'Amortização SAC mal aplicada', b: 'Cálculos de amortização que não respeitam o saldo devedor decrescente, resultando em juros acumulados muito além do contratado.' },
        { t: 'Tarifas administrativas indevidas', b: 'Avaliação do imóvel, registro e administração cobrados de forma duplicada ou em valores acima dos limites do BACEN.' },
        { t: 'Taxas acima do teto BACEN', b: 'Contratos com juros efetivos superiores à média do mercado para o período, ferindo a Lei 4.380/64 e a jurisprudência consolidada do STJ.' },
      ],
      reviews: [
        { n: 'Roberto Almeida', t: 'Financiei meu apartamento na Caixa em 2015. O diagnóstico apontou abuso de R$ 187 mil ao longo do contrato. Estou em ação revisional.' },
        { n: 'Cláudia Ferreira', t: 'Sempre desconfiei do MIP/DFI cobrado no meu financiamento. O relatório mostrou que paguei 3x mais que a média. Recuperei R$ 64 mil.' },
        { n: 'Eng. Marcelo Tavares', t: 'Como engenheiro, refiz os cálculos do PDF e bateu até a última casa decimal. Documento técnico de altíssima qualidade. Vale cada centavo.' },
      ],
      faq: [
        { q: 'Como sei se meu financiamento imobiliário tem juros abusivos?', a: 'Comparamos a taxa de juros do seu contrato com a série oficial do BACEN para financiamento imobiliário (25499 PF / 25488 PJ). Pela jurisprudência do STJ e Lei 4.380/64, taxas substancialmente superiores à média de mercado são passíveis de revisão judicial.' },
        { q: 'A análise vale para SAC e Tabela Price?', a: 'Sim. O sistema detecta automaticamente o sistema de amortização (SAC ou Price) e aplica a fórmula correta. Cada um tem padrões de abuso distintos — em SAC é comum manipulação do saldo devedor; em Price, capitalização incorreta.' },
        { q: 'Quais informações preciso ter em mãos?', a: 'Valor financiado, número total de parcelas (ex.: 360 = 30 anos), valor da 1ª parcela (ou da parcela fixa em Price), data do contrato e o banco. Para SAC, use o valor da primeira parcela — o sistema calcula o restante.' },
        { q: 'O diagnóstico cobre MIP/DFI e tarifas administrativas?', a: 'O diagnóstico foca na taxa de juros efetiva. MIP/DFI e tarifas são apontados qualitativamente no parecer; auditoria detalhada desses itens requer análise extra do contrato físico.' },
        { q: 'Posso usar o diagnóstico para entrar com ação revisional?', a: 'Sim. O documento é elaborado com base nas séries oficiais do BACEN e jurisprudência do STJ, com memória de cálculo detalhada. É admitido como prova técnica em ações revisionais de financiamento imobiliário.' },
        { q: 'Em quanto tempo recebo o diagnóstico?', a: 'Acesso imediato após confirmação do pagamento. PDF disponível na tela e enviado por e-mail. Link de download seguro válido por 7 dias.' },
        { q: 'E se meu contrato não for abusivo?', a: 'Você recebe o diagnóstico mesmo assim, com comprovação técnica de conformidade. Útil para quem quer ter certeza antes de buscar revisão judicial.' },
        { q: 'Precisa de advogado para usar o diagnóstico?', a: 'Não para gerar o documento. Para ação revisional imobiliária, recomendamos advogado especializado em Direito Bancário/Imobiliário. O Dr. Cristian Lessa atende esses casos.' },
      ],
      roi: {
        prejuizoMedio: 'R$ 87.000',
        prejuizoLabel: 'Prejuízo médio detectado em contratos imobiliários abusivos',
      },
    },
  },
}

export function getProduct(slug) {
  const p = products[slug]
  if (!p) throw new Error(`Unknown product slug: ${slug}`)
  return p
}

export function getBacenCollection(product, tipo) {
  if (tipo !== 'PF' && tipo !== 'PJ') throw new Error(`Invalid tipo: ${tipo}`)
  return product.collections[tipo]
}

export function getLeadsCollection(product, tipo) {
  if (tipo !== 'PF' && tipo !== 'PJ') throw new Error(`Invalid tipo: ${tipo}`)
  return product.collections[tipo === 'PF' ? 'leadsPF' : 'leadsPJ']
}

export function getProductByLeadCollection(leadCollection) {
  for (const p of Object.values(products)) {
    if (p.collections.leadsPF === leadCollection || p.collections.leadsPJ === leadCollection) return p
  }
  return null
}

export function formatBRL(value) {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export async function hydrateProductPrices(db, fsApi) {
  try {
    const snap = await fsApi.getDocs(fsApi.collection(db, 'products_config'))
    snap.forEach((d) => {
      const cfg = d.data()
      if (products[d.id] && typeof cfg.priceBRL === 'number' && cfg.priceBRL > 0) {
        products[d.id].priceBRL = cfg.priceBRL
      }
    })
  } catch (e) {
    console.warn('[products] price hydration failed', e?.code || e?.message)
  }
}
